import { useState } from 'react';
import { toast } from 'sonner';
import { CloudDownload, Play, Loader2 } from 'lucide-react';
import { simulate } from '../lib/aquaNet';
import { loadClimate, thermal, tWarn, soilAt, seasonClim } from '../lib/aquaTemp';
import { setAqua, useAqua } from '../lib/aquaStore';
import { SourcePick } from './AquaSourcePick';

const n1 = (v, d = 1) => (v == null || !Number.isFinite(v) ? '–' : v.toFixed(d).replace('.', ','));

// water temperature + stagnation: source temperature, soil temperature (climate data or manual), consumers
export function AquaTemp({ t, project, updateProject, cfg, setCfg }) {
  const s = useAqua();
  const [busy, setBusy] = useState(false);
  const clim = cfg.clim || null, scen = cfg.scen || 'now';
  const soil = clim ? { ...clim, t54: scen === 'summer' ? clim.max54 ?? clim.t54 : scen === 'winter' ? clim.min54 ?? clim.t54 : clim.t54 } : seasonClim(scen);
  const useClim = cfg.manualSoil === '' || cfg.manualSoil == null ? soil : { manual: +cfg.manualSoil };
  const load = async () => {
    const f = (project.fittings || [])[0], c = f ? [f.lat, f.lon] : project.pin;
    if (!c) return toast.error(t.aqNoPos);
    setBusy(true);
    try { setCfg({ clim: await loadClimate(+c[0], +c[1]) }); } catch { toast.error(t.aqClimErr); }
    setBusy(false);
    return null;
  };
  const run = async () => {
    setBusy(true);
    try {
      const res = await simulate(project, cfg);
      if (res.error) { if (res.error === 'noSource') setAqua({ res, th: null }); else toast.error(t['aqErr_' + res.error] || t.aqErr_calc); setBusy(false); return; }
      setAqua({ res, th: thermal(res, cfg, useClim), on: true, mode: 't' });
    } catch (e) { toast.error(String(e?.message || e)); }
    setBusy(false);
  };
  const th = s.th, byId = Object.fromEntries((project.fittings || []).map((f) => [f.id, f]));
  const ends = th ? [...th.endT].sort((a, b) => b.t - a.t).slice(0, 12) : [];
  const stag = th ? Object.values(th.links).filter((x) => x.stag && x.tOut != null) : [];
  return (
    <>
      <p className="aq-est" data-testid="aq-temp-est">{t.aqTempEst}</p>
      <p className="muted">{t.aqTempHint}</p>
      <div className="aq-grid">
        <label>{t.aqTin}<input type="number" step="0.1" data-testid="aq-tin" value={cfg.tIn ?? 10} onChange={(e) => setCfg({ tIn: e.target.value })} /> °C</label>
        <label>{t.aqU}<input type="number" step="0.1" data-testid="aq-u" value={cfg.U ?? 2.5} onChange={(e) => setCfg({ U: e.target.value })} /> W/m²K</label>
        <label>{t.aqScen}<select data-testid="aq-scen" value={scen} onChange={(e) => setCfg({ scen: e.target.value })}><option value="now">{t.aqScen_now}</option><option value="summer">{t.aqScen_summer}</option><option value="winter">{t.aqScen_winter}</option></select></label>
        <label>{t.aqManSoil}<input type="number" step="0.1" placeholder={t.aqAuto} data-testid="aq-manual-soil" value={cfg.manualSoil ?? ''} onChange={(e) => setCfg({ manualSoil: e.target.value })} /> °C</label>
      </div>
      <div className="aq-row">
        <button className="btn btn-outline" data-testid="aq-clim-load" disabled={busy || !navigator.onLine} onClick={load}><CloudDownload size={16} /> {t.aqClimLoad}</button>
        {clim && <small data-testid="aq-clim-info">{t.aqAir} {n1(clim.air)} °C · {t.aqSoil54} {n1(clim.t54)} °C · {t.aqMean} {n1(clim.mean)} °C · {t.aqSoilAt} {n1(soilAt(useClim, 1.5))} °C</small>}
        {!clim && useClim.est && <small className="aq-warn" data-testid="aq-soil-est">{t.aqSoilEst.replace('{t}', n1(useClim.t54))}</small>}
      </div>
      <button className="btn btn-accent aq-run" data-testid="aq-temp-run" disabled={busy} onClick={run}>{busy ? <Loader2 size={16} className="spin" /> : <Play size={16} />} {t.aqTempRun}</button>
      {s.res?.error === 'noSource' && <SourcePick t={t} project={project} updateProject={updateProject} />}
      {th && (
        <div className="aq-res" data-testid="aq-temp-result">
          <div className="aq-kpi"><b data-testid="aq-tmax">{n1(th.min)} – {n1(th.max)} °C</b><small>{t.aqTrange}</small></div>
          {stag.length > 0 && <small className="muted" data-testid="aq-stag-info">{t.aqStagInfo.replace('{n}', stag.length).replace('{t}', n1(stag[0].tOut))}</small>}
          {ends.map((e) => { const w = tWarn(e.t); return (
            <div key={e.id} className={`aq-row ${w ? 'aq-w-' + w : ''}`} data-testid={`aq-end-${e.fid || e.id}`}>
              <span>{byId[e.fid]?.name || t['fit_' + byId[e.fid]?.type] || e.id}</span><b>{n1(e.t)} °C</b><small>{Number.isFinite(e.h) ? `${n1(e.h)} h` : t.aqStag}</small>{w && <small>{t['aqW_' + w]}</small>}
            </div>
          ); })}
          {th.max >= 20 && <p className="aq-warn" data-testid="aq-hot-warn">{t.aqHot}</p>}
          {th.min <= 0.5 && <p className="aq-warn" data-testid="aq-frost-warn">{t.aqFrost}</p>}
          <p className="aq-disc">{t.aqTempDisc}</p>
        </div>
      )}
    </>
  );
}
